import { Bouton } from '../ui/composants/bouton'
import { AtSign, Building2, Hash, Mail, MapPin, Phone, UserRound, type LucideIcon } from 'lucide-react'
import { EnteteSection } from '../ui/composants/entete-section'
import { useLiveQuery } from 'dexie-react-hooks'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { donneesClient, erreursClient, ficheDe, type FicheClient } from '../lib/clients'
import { getBase } from '../outbox/base'
import { lire, modifier } from '../outbox/depot'
import { BarreActions, CHAMP } from '../ui/synchro'

const CHAMPS: { cle: keyof FicheClient; libelle: string; mode?: 'tel' | 'numeric' | 'email'; aide?: string }[] = [
  { cle: 'nom', libelle: 'Nom du client' },
  { cle: 'telephone', libelle: 'Téléphone', mode: 'tel' },
  { cle: 'email', libelle: 'E-mail', mode: 'email', aide: 'Préremplit le destinataire des envois et des relances.' },
  { cle: 'adresse', libelle: 'Adresse' },
  { cle: 'codePostal', libelle: 'Code postal', mode: 'numeric' },
  { cle: 'ville', libelle: 'Ville' },
  { cle: 'siret', libelle: 'SIRET', mode: 'numeric', aide: '14 chiffres. Exigé pour facturer une entreprise.' },
  { cle: 'adresseElectronique', libelle: 'Adresse électronique (annuaire)', aide: 'Facultative : « SIREN » ou « SIREN_suffixe » dans l’annuaire des plateformes agréées. Sans elle, le SIREN du SIRET est utilisé.' },
]

const ICONES: Record<keyof FicheClient, LucideIcon> = { nom: UserRound, telephone: Phone, email: Mail, adresse: MapPin, codePostal: Hash, ville: MapPin, siret: Building2, adresseElectronique: AtSign }

function Formulaire(props: { id: string; initiale: FicheClient }) {
  const base = getBase()
  const naviguer = useNavigate()
  const [f, setF] = useState(props.initiale)
  const [verifie, setVerifie] = useState(false)
  const erreurs = erreursClient(f)

  async function enregistrer() {
    setVerifie(true)
    if (Object.keys(erreurs).length > 0) return
    await modifier(base, 'clients', props.id, donneesClient(f))
    naviguer(-1)
  }

  return (
    <>
      <main className="flex flex-1 flex-col gap-4 p-4">
        <EnteteSection
          Icone={UserRound}
          titre="Fiche client"
          description={
            <>
              L’adresse et le SIRET figurent sur les factures électroniques.{' '}
              <button type="button" className="min-h-0 underline" onClick={() => naviguer(-1)}>
                Retour
              </button>
            </>
          }
        />
        {CHAMPS.map((c) => {
          const erreur = verifie ? erreurs[c.cle] : undefined
          return (
            <label key={c.cle} className="flex flex-col gap-1 text-sm">
              <span className="flex items-center gap-2 font-semibold">
                {(() => {
                  const Icone = ICONES[c.cle]
                  return <Icone aria-hidden className="size-5" />
                })()}
                {c.libelle}
              </span>
              <input
                className={`${CHAMP} ${erreur ? 'border-red-700' : ''}`}
                value={f[c.cle]}
                inputMode={c.mode}
                aria-invalid={erreur ? true : undefined}
                onChange={(ev) => setF({ ...f, [c.cle]: ev.target.value })}
              />
              {erreur ? (
                <span className="text-red-800" role="alert">
                  {erreur}
                </span>
              ) : (
                c.aide && <span className="text-slate-700">{c.aide}</span>
              )}
            </label>
          )
        })}
      </main>
      <BarreActions>
        <Bouton className="flex-1" onClick={enregistrer}>
          Enregistrer
        </Bouton>
      </BarreActions>
    </>
  )
}

export default function FicheClientPage() {
  const { id = '' } = useParams()
  const client = useLiveQuery(() => lire(getBase(), 'clients', id), [id])
  if (client === undefined) return <main className="flex-1 p-4" />
  if (client === null) {
    return (
      <main className="flex flex-1 flex-col gap-4 p-4">
        <p>Ce client n’existe pas ou a été supprimé.</p>
        <Link to="/" className="underline">
          Retour
        </Link>
      </main>
    )
  }
  return <Formulaire id={id} initiale={ficheDe(client)} />
}
